/**
 * Extração bruta do PDF: itens de texto com coordenada, e a imagem da faca.
 *
 * **Por que coordenada e não ordem de conteúdo.** A OS é um formulário de
 * tabelas. Em ordem de conteúdo, uma célula que quebra em duas linhas
 * ("MULTILITH" / "NUMERADOR") vira uma linha solta, e uma coluna vazia some sem
 * deixar rastro — o parser passa a adivinhar por nome de equipamento, e um
 * equipamento novo desloca tudo em silêncio. Pela posição, cada célula sabe a
 * que coluna pertence mesmo que o texto dentro dela seja inédito.
 *
 * O texto em ordem de conteúdo continua sendo extraído: é ele que vira o hash
 * da versão da OS (ANATOMIA §4).
 */

export interface ItemDeTexto {
  /** Distância da borda esquerda, em pontos. */
  x: number;
  /** Distância do TOPO da página, em pontos (pdf.js mede da base). */
  y: number;
  largura: number;
  texto: string;
}

export interface PaginaLida {
  itens: ItemDeTexto[];
  /** Texto na ordem em que foi escrito no PDF. Base do hash. */
  textoEmOrdem: string;
  largura: number;
  altura: number;
}

export interface PdfLido {
  paginas: PaginaLida[];
  /** Todos os itens de todas as páginas, com `y` deslocado por página. */
  itens: ItemDeTexto[];
  textoEmOrdem: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type ModuloPdfjs = any;

let moduloInjetado: ModuloPdfjs | null = null;

/**
 * O navegador precisa configurar o worker antes de ler o primeiro PDF; o Node
 * não precisa de nada. Em vez de o leitor adivinhar onde está rodando, quem
 * hospeda entrega o módulo já configurado.
 */
export function configurarPdfjs(modulo: ModuloPdfjs): void {
  moduloInjetado = modulo;
}

async function carregarPdfjs(): Promise<ModuloPdfjs> {
  if (moduloInjetado) return moduloInjetado;
  // A build "legacy" é a que roda em Node sem worker e no navegador sem
  // depender de recursos novos demais.
  moduloInjetado = await import(/* @vite-ignore */ 'pdfjs-dist/legacy/build/pdf.mjs');
  return moduloInjetado;
}

/** Lê o PDF inteiro: itens posicionados por página e o texto em ordem. */
export async function lerPdf(dados: Uint8Array): Promise<PdfLido> {
  const pdfjs = await carregarPdfjs();
  const doc = await pdfjs.getDocument({
    // pdf.js pode consumir o buffer; uma cópia evita surpresa em quem chamou.
    data: new Uint8Array(dados),
    useSystemFonts: true,
    disableFontFace: true,
    isEvalSupported: false,
    verbosity: 0,
  }).promise;

  const paginas: PaginaLida[] = [];
  const todos: ItemDeTexto[] = [];
  const textos: string[] = [];
  let deslocamentoY = 0;

  try {
    for (let n = 1; n <= doc.numPages; n += 1) {
      const pagina = await doc.getPage(n);
      const vista = pagina.getViewport({ scale: 1 });
      const conteudo = await pagina.getTextContent();

      const itens: ItemDeTexto[] = [];
      const emOrdem: string[] = [];

      for (const item of conteudo.items as Array<Record<string, unknown>>) {
        const bruto = typeof item['str'] === 'string' ? (item['str'] as string) : '';
        const texto = bruto.trim();
        if (!texto) continue;
        const t = item['transform'] as number[] | undefined;
        if (!t) continue;
        itens.push({
          x: arredondar(t[4] ?? 0),
          y: arredondar(vista.height - (t[5] ?? 0)),
          largura: arredondar((item['width'] as number) ?? 0),
          texto,
        });
        emOrdem.push(texto);
      }

      const texto = emOrdem.join('\n');
      paginas.push({ itens, textoEmOrdem: texto, largura: vista.width, altura: vista.height });
      textos.push(texto);
      for (const i of itens) todos.push({ ...i, y: i.y + deslocamentoY });
      deslocamentoY += vista.height;
      pagina.cleanup();
    }
  } finally {
    await doc.destroy();
  }

  return { paginas, itens: todos, textoEmOrdem: textos.join('\n') };
}

function arredondar(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * A imagem embutida (o desenho da faca com a foto da faca física).
 *
 * O PDF da gráfica é 1.2, sem fluxo de objetos e com a imagem em `DCTDecode`
 * — JPEG já pronto. Dá para recortar os bytes direto, sem depender do
 * renderizador do pdf.js, que exigiria canvas e recodificaria a imagem.
 *
 * Devolve `null` quando não há imagem ou quando os bytes não são um JPEG
 * íntegro; é anexo, não pode derrubar a importação de uma OS.
 */
export function extrairImagemJpeg(dados: Uint8Array): Uint8Array | null {
  const marca = acharSequencia(dados, '/DCTDecode');
  if (marca < 0) return null;

  const inicioStream = acharSequencia(dados, 'stream', marca);
  if (inicioStream < 0) return null;

  // Depois de "stream" vem \r\n, \n ou \r.
  let p = inicioStream + 'stream'.length;
  if (dados[p] === 0x0d) p += 1;
  if (dados[p] === 0x0a) p += 1;

  // Um JPEG começa em FFD8 e termina em FFD9.
  if (dados[p] !== 0xff || dados[p + 1] !== 0xd8) return null;
  const fim = acharFimJpeg(dados, p);
  if (fim < 0) return null;

  return dados.slice(p, fim + 2);
}

function acharSequencia(dados: Uint8Array, alvo: string, de = 0): number {
  const b = new TextEncoder().encode(alvo);
  const limite = dados.length - b.length;
  for (let i = de; i <= limite; i += 1) {
    let bate = true;
    for (let k = 0; k < b.length; k += 1) {
      if (dados[i + k] !== b[k]) {
        bate = false;
        break;
      }
    }
    if (bate) return i;
  }
  return -1;
}

function acharFimJpeg(dados: Uint8Array, de: number): number {
  for (let i = de + 2; i < dados.length - 1; i += 1) {
    if (dados[i] === 0xff && dados[i + 1] === 0xd9) return i;
  }
  return -1;
}
